import { loadConfig } from "./core/config";
import { ApiClient } from "./core/api-client";
import { resolveApiBase, assetUrl } from "./core/origin";
import { selectFeature, checkMicrophonePermission, checkVoiceUsable, FEATURE_LABELS } from "./core/feature-selector";
import { autoCollect, collectVisitorInfo } from "./core/visitor-info";
import { mountContainer, setActiveFeature, setFabPulse, getShadowRoot, setPanelContent, setFloatingIcons, showPanelBack, resetContainer } from "./ui/container";
import { t } from "./ui/i18n";
import { renderCallbackPanel } from "./modules/callback/render";
import { renderVoicePanel } from "./modules/voice/render";
import { renderTextChatPanel } from "./modules/text-chat/render";
import type { InitOptions, WidgetConfig, Feature, SDKState } from "./types";

var state: SDKState = { config: null, activeFeature: null, panelOpen: false, micAvailable: null, initialized: false, error: null };
var _apiBase = resolveApiBase(), _visitorId = "", _heartbeatTimer: any = null, _apiClient: ApiClient | null = null, _debug = false;
var _initializing = false, _initPromise: Promise<void> | null = null;
function log(...args: unknown[]): void { console.log("[OneCallSDK]", ...args); }

export async function init(opts: InitOptions): Promise<void> {
  // 已初始化但 DOM 被外部移除（如 React Strict Mode），恢复挂载
  if (state.initialized) {
    if (!document.getElementById("onecall-widget-host")) {
      console.warn("[OneCallSDK] DOM 丢失，重新挂载");
      resetContainer();
      mountContainer(state.config!.theme, {
        onPanelOpen: function(feature?: Feature, ch?: string) {
          state.panelOpen = true;
          if (feature) { state.activeFeature = feature; setActiveFeature(feature); renderActiveFeature(feature); }
          else { renderHomeView(); }
        },
        onPanelClose: function() { state.panelOpen = false; },
      }, state.config!.buttonLogo, state.config!.aiAvatar);
      setActiveFeature(state.activeFeature);
    } else {
      console.warn("[OneCallSDK] 已初始化，跳过");
    }
    return;
  }
  if (_initializing) { console.warn("[OneCallSDK] 正在初始化，等待完成"); return _initPromise!; }
  _initializing = true;
  _initPromise = _doInit(opts);
  try { await _initPromise; } finally { _initializing = false; }
}

async function _doInit(opts: InitOptions): Promise<void> {
  _debug = opts.debug || (opts.config as any)?.debug || false;
  log("初始化...", opts);
  try {
    _apiBase = opts.apiBase || _apiBase; _apiClient = new ApiClient(_apiBase);
    if (typeof window !== "undefined") (window as any).__oc_apiBase__ = _apiBase;
    var vi = autoCollect(); _visitorId = vi.visitorId; log("访客信息:", _visitorId);
    state.config = await loadConfig(opts, _apiBase); log("配置:", state.config.features);
    try { await _apiClient.submitLead({ widgetId: opts.widgetId, visitorId: _visitorId, actionType: "page_view", pageUrl: vi.pageUrl, referrer: vi.referrer, utmParams: { source: vi.utm.source, medium: vi.utm.medium, campaign: vi.utm.campaign, term: vi.utm.term, content: vi.utm.content }, device: vi.deviceJson, browser: vi.browserJson }); } catch {}
    _heartbeatTimer = setInterval(function() { _apiClient!.sendHeartbeat(_visitorId, opts.widgetId).catch(function() {}); }, 30000);
    state.micAvailable = await checkMicrophonePermission().catch(function() { return false; });
    var result = await selectFeature(state.config.features, { voiceFallbackToCallback: state.config.voiceFallbackToCallback });
    state.activeFeature = result.feature; log("功能:", result);
    // 展示方式：expanded 在小屏(<480px)回退为 collapsed，避免遮挡正文
    var smallScreen = typeof window !== "undefined" && window.innerWidth < 480;
    var displayMode = (state.config.displayMode === "expanded" && !smallScreen) ? "expanded" : "collapsed";
    mountContainer(state.config.theme, {
      onPanelOpen: function(feature?: Feature, ch?: string) {
        state.panelOpen = true;
        if (feature) { state.activeFeature = feature; setActiveFeature(feature); renderActiveFeature(feature); }
        else { renderHomeView(); }
      },
      onPanelClose: function() { state.panelOpen = false; },
      onImClick: function(channelId: string, value: string, name: string) { goToIM(channelId, value, name); },
    }, state.config.buttonLogo, state.config.aiAvatar, displayMode);
    if (displayMode === "expanded") {
      var imIcons = (state.config.imChannels || []).filter(function(ch: any) { return ch.enabled; }).map(function(ch: any) { return { id: ch.id, name: imChannelName(ch.id, ch.name), icon: ch.icon || "💬", value: ch.value || "" }; });
      setFloatingIcons(state.config.features, imIcons, {
        showLabels: state.config.expandedShowLabels !== false,
        direction: state.config.expandedDirection === "horizontal" ? "horizontal" : "vertical",
        order: state.config.expandedOrder || [],
      });
    }
    setActiveFeature(state.activeFeature);
    state.initialized = true; state.error = null; startWidgetGuard();
    log("初始化完成, 激活:", state.activeFeature ? FEATURE_LABELS[state.activeFeature] : "无");
  } catch (e) { state.error = e instanceof Error ? e.message : String(e); console.error("[OneCallSDK] 失败:", state.error); state.initialized = false; }
}

var _observer: any = null, _ot: any = null, _ore = false, _oreCount = 0, _oreCooldown = 0;
function startWidgetGuard(): void {
  if (_observer) return;
  _observer = new MutationObserver(function() {
    if (_ore) return;
    if (_ot) return;
    var now = Date.now();
    if (now < _oreCooldown) return;
    _ot = setTimeout(function() {
      _ot = null;
      if (!document.getElementById("onecall-widget-host")) {
        _oreCount++;
        if (_oreCount > 5) { _oreCooldown = now + 30000; _oreCount = 0; return; }
        _ore = true;
        try {
          log("恢复...");
          mountContainer(state.config!.theme, { onPanelOpen: function() { state.panelOpen = true; renderHomeView(); }, onPanelClose: function() { state.panelOpen = false; } }, state.config!.buttonLogo, state.config!.aiAvatar);
          setActiveFeature(state.activeFeature);
          _oreCount = 0;
        } finally { _ore = false; }
      }
    }, 500);
  });
  _observer.observe(document.body, { childList: true, subtree: true });
}
function stopWidgetGuard(): void { if (_ot) { clearTimeout(_ot); _ot = null; } if (_observer) { _observer.disconnect(); _observer = null; } }

/** 统一 SVG 线性图标（替代 emoji，C4/F4） */
var ICON = {
  mic: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10v1a7 7 0 0014 0v-1"/><line x1="12" y1="18" x2="12" y2="22"/></svg>',
  chat: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>',
  phone: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>',
  botFilled: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="8" width="16" height="11" rx="3"/><path d="M12 8V4"/><circle cx="12" cy="3" r="1.2" fill="currentColor"/><circle cx="9" cy="13" r="1.3" fill="currentColor" stroke="none"/><circle cx="15" cy="13" r="1.3" fill="currentColor" stroke="none"/></svg>',
};

/** 启动页两行宽按钮（标题 + 副标题 + SVG 图标） */
function homeBtn(id: string, c: string, icon: string, label: string, sub: string): string {
  return '<button id="' + id + '" style="display:flex;align-items:center;gap:12px;width:100%;padding:13px 14px;border:1px solid #e8ecf1;border-radius:12px;background:#f8fafd;cursor:pointer;margin-bottom:8px;color:var(--oc-text);text-align:left" onmouseover="this.style.background=\'#eef2ff\';this.style.borderColor=\'' + c + '\'" onmouseout="this.style.background=\'#f8fafd\';this.style.borderColor=\'#e8ecf1\'">' +
    '<span style="width:34px;height:34px;border-radius:9px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:' + c + '18;color:' + c + '">' + icon + '</span>' +
    '<span style="flex:1;display:flex;flex-direction:column"><span style="font-size:14px;font-weight:600">' + label + '</span><span style="font-size:12px;color:var(--oc-text-secondary);margin-top:1px">' + sub + '</span></span>' +
    '<span style="color:#bbb;font-size:18px">›</span></button>';
}

function renderHomeView(): void {
  if (!state.config) return;
  showPanelBack(false);
  var cfg = state.config;
  var loc = cfg.theme.locale || "zh-CN";
  var c = getComputedStyle(getShadowRoot()!.host).getPropertyValue("--oc-primary").trim() || "#533afd";
  var imChannels = (cfg.imChannels || []).filter(function(ch: any) { return ch.enabled; });
  var features = cfg.features || [];
  var hasVoice = features.indexOf("voice") !== -1;
  var hasTextChat = features.indexOf("text_chat") !== -1;
  var hasCallback = features.indexOf("callback") !== -1;
  var hasIM = features.indexOf("im") !== -1 && imChannels.length > 0;

  var h = '<div style="display:flex;flex-direction:column;height:100%"><div style="flex:1;overflow-y:auto;padding:16px">';

  // C5：接待员卡片（头像 + 名称 + 在线状态），建立信任
  var recName = esc(cfg.receptionistName || cfg.theme.title || t("home.recName", loc));
  var avatarInner = cfg.aiAvatar
    ? '<img src="' + esc(cfg.aiAvatar) + '" alt="" style="width:100%;height:100%;border-radius:50%;object-fit:cover" />'
    : ICON.botFilled;
  h += '<div style="display:flex;align-items:center;gap:12px;padding:14px;background:#fff;border:1px solid var(--oc-border);border-radius:14px;margin-bottom:14px">' +
         '<div style="width:44px;height:44px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:' + c + ';color:#fff;overflow:hidden">' + avatarInner + '</div>' +
         '<div style="line-height:1.4"><div style="font-size:15px;font-weight:600;color:var(--oc-text)">' + recName + '</div>' +
           '<div style="font-size:12px;color:#15803d;display:flex;align-items:center;gap:5px"><span style="width:7px;height:7px;border-radius:50%;background:#22c55e;display:inline-block"></span>' + t("home.online", loc) + '</div></div>' +
       '</div>';

  if (hasVoice) h += homeBtn("hm-voice", c, ICON.mic, t("home.voice", loc), t("home.voiceSub", loc));
  if (hasTextChat) h += homeBtn("hm-text", c, ICON.chat, t("home.text", loc), t("home.textSub", loc));
  if (hasCallback) h += homeBtn("hm-callback", c, ICON.phone, t("home.callback", loc), t("home.callbackSub", loc));

  if (hasIM) {
    h += '<div><div style="font-size:11px;color:var(--oc-text-secondary);margin-bottom:8px">' + t("home.imSection", loc) + '</div><div style="background:#fff;border:1px solid var(--oc-border);border-radius:14px;overflow:hidden">';
    for (var i = 0; i < imChannels.length; i++) {
      var ch = imChannels[i];
      var chName = imChannelName(ch.id, ch.name); // 存库无 name → 按 id 解析，避免显示 undefined
      // J1/J2：改为可点击行，点击时先提交线索再跳转（微信走二维码浮层），不再用直接外链
      h += '<div class="oc-im-row" data-channel="' + esc(ch.id) + '" data-value="' + esc(ch.value || "") + '" data-name="' + esc(chName) + '" style="display:flex;align-items:center;padding:13px 14px;cursor:pointer;color:var(--oc-text);border-bottom:1px solid #f5f5f5" onmouseover="this.style.background=\'#f9fafb\'" onmouseout="this.style.background=\'#fff\'"><div style="display:flex;align-items:center;gap:10px;flex:1"><img src="' + assetUrl('/imlogo/' + ch.id + '.svg') + '" style="width:36px;height:36px;border-radius:6px" /><span style="font-size:14px">' + esc(chName) + '</span></div><span style="color:#bbb;font-size:18px">›</span></div>';
    }
    h += '</div></div>';
  }
  h += '</div></div>';
  setPanelContent(h);
  var root = getShadowRoot(); if (!root) return;
  root.getElementById("hm-voice")?.addEventListener("click", function() { renderFeature("voice"); });
  root.getElementById("hm-text")?.addEventListener("click", function() { renderFeature("text_chat"); });
  root.getElementById("hm-callback")?.addEventListener("click", function() { renderFeature("callback"); });
  root.querySelectorAll(".oc-im-row").forEach(function(row: any) {
    row.addEventListener("click", function() {
      goToIM(row.getAttribute("data-channel") || "", row.getAttribute("data-value") || "", row.getAttribute("data-name") || "");
    });
  });
}

/** IM 通道显示名（按 id 解析）。存库的 imChannels 只有 {id,enabled,value}，无 name，
 *  直接渲染 ch.name 会显示成 "undefined"。 */
var IM_NAMES: Record<string, string> = {
  whatsapp: "WhatsApp", messenger: "Facebook Messenger", instagram: "Instagram",
  telegram: "Telegram", line: "LINE", wechat: "微信", zalo: "Zalo", viber: "Viber",
};
function imChannelName(id: string, fallback?: string): string {
  return IM_NAMES[id] || fallback || id;
}

/** IM 跳转链接模板（业务联系方式作为目标） */
var IM_URLS: Record<string, (v: string) => string> = {
  whatsapp: function(v) { return "https://wa.me/" + v.replace(/\D/g, ""); },
  messenger: function(v) { return "https://m.me/" + v; },
  instagram: function(v) { return "https://ig.me/m/" + v; },
  telegram: function(v) { return "https://t.me/" + v.replace(/^@/, ""); },
  line: function(v) { return "https://line.me/R/ti/p/" + v; },
  zalo: function(v) { return "https://zalo.me/" + v.replace(/\D/g, ""); },
  viber: function(v) { return "viber://chat?number=" + encodeURIComponent(v); },
};

/** J2：跳转前提交线索，避免从启动页走 IM 丢失访客信息 */
function submitImLead(channelId: string, channelName: string): void {
  try {
    var info = collectVisitorInfo();
    var client = _apiClient || new ApiClient(_apiBase);
    client.submitLead({
      widgetId: state.config?.widgetId || "",
      visitorId: info.visitorId,
      actionType: "im",
      actionLabel: channelName || channelId,
      channel: channelId,
      name: info.referrer || "",
      pageUrl: info.pageUrl,
      referrer: info.referrer,
      utmParams: info.utm,
      device: info.deviceJson,
      browser: info.browserJson,
    }).catch(function() { /* 静默失败，不阻断跳转 */ });
  } catch (e) { /* ignore */ }
}

function imLooksLikeImage(v: string): boolean {
  return /^https?:\/\//i.test(v) && /\.(png|jpe?g|svg|webp|gif)$/i.test(v.split("?")[0]);
}

/** J1：微信不再点击无反应，弹出二维码/微信号浮层 */
function showWechatOverlay(value: string): void {
  var root = getShadowRoot(); if (!root) return;
  var old = root.getElementById("oc-wx-overlay"); if (old) old.remove();
  var c = getComputedStyle(root.host).getPropertyValue("--oc-primary").trim() || "#533afd";
  var loc = state.config?.theme.locale || "zh-CN";

  var inner: string;
  if (imLooksLikeImage(value)) {
    inner = '<img src="' + esc(value) + '" alt="' + t("home.wechatTitle", loc) + '" style="width:180px;height:180px;object-fit:contain;border-radius:8px;border:1px solid var(--oc-border)" /><p style="font-size:12px;color:var(--oc-text-secondary);margin:12px 0 0">' + t("home.wechatScan", loc) + '</p>';
  } else if (/^https?:\/\//i.test(value)) {
    try { window.open(value, "_blank", "noopener"); } catch (e) { /* ignore */ }
    inner = '<p style="font-size:13px;color:var(--oc-text);margin:0 0 8px">' + t("home.wechatOpened", loc) + '</p><a href="' + esc(value) + '" target="_blank" rel="noopener" style="font-size:13px;color:' + c + ';text-decoration:none">' + t("home.wechatFallback", loc) + '</a>';
  } else if (value) {
    inner = '<div style="font-size:18px;font-weight:600;color:var(--oc-text);background:#f5f7fa;border-radius:10px;padding:12px;margin-bottom:12px;word-break:break-all;user-select:all">' + esc(value) + '</div><button type="button" id="oc-wx-copy" style="padding:9px 22px;border:none;border-radius:20px;background:' + c + ';color:#fff;font-size:14px;cursor:pointer;margin-bottom:8px">' + t("home.wechatCopy", loc) + '</button><a href="weixin://dl/business/?t=' + encodeURIComponent(value) + '" style="display:block;font-size:13px;color:' + c + ';text-decoration:none;margin-top:8px">' + t("home.wechatOpenApp", loc) + '</a><p style="font-size:12px;color:var(--oc-text-secondary);margin:12px 0 0;line-height:1.5">' + t("home.wechatSearchHint", loc) + '</p>';
  } else {
    inner = '<p style="font-size:12px;color:var(--oc-text-secondary);margin:0">' + t("home.wechatNone", loc) + '</p>';
  }

  var overlay = document.createElement("div");
  overlay.id = "oc-wx-overlay";
  overlay.setAttribute("style", "position:absolute;inset:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:50");
  overlay.innerHTML = '<div style="position:relative;background:#fff;border-radius:16px;padding:24px 20px 20px;width:84%;max-width:300px;text-align:center;box-shadow:0 12px 40px rgba(0,0,0,0.25)"><button type="button" id="oc-wx-close" aria-label="' + t("container.close", loc) + '" style="position:absolute;top:8px;right:10px;border:none;background:transparent;font-size:24px;line-height:1;color:#999;cursor:pointer">&#215;</button><div style="font-size:15px;font-weight:600;color:var(--oc-text);margin-bottom:14px">' + t("home.wechatTitle", loc) + '</div>' + inner + '</div>';

  var body = root.getElementById("oc-body"); if (!body) return;
  if (getComputedStyle(body).position === "static") body.style.position = "relative";
  body.appendChild(overlay);
  overlay.addEventListener("click", function(e: any) { if (e.target === overlay) overlay.remove(); });
  root.getElementById("oc-wx-close")?.addEventListener("click", function() { overlay.remove(); });
  root.getElementById("oc-wx-copy")?.addEventListener("click", function() {
    try {
      navigator.clipboard?.writeText(value);
      var btn = root!.getElementById("oc-wx-copy");
      if (btn) { btn.textContent = t("home.wechatCopied", loc); setTimeout(function() { var b = root!.getElementById("oc-wx-copy"); if (b) b.textContent = t("home.wechatCopy", loc); }, 1500); }
    } catch (e) { /* ignore */ }
  });
}

/** IM 行点击：先采集线索，再跳转 / 弹微信浮层 */
function goToIM(channelId: string, value: string, name: string): void {
  submitImLead(channelId, name);
  if (channelId === "wechat") { showWechatOverlay(value || ""); return; }
  var fn = IM_URLS[channelId];
  if (fn && value) window.open(fn(value), "_blank", "noopener");
}

function renderFeature(feature: Feature): void {
  if (!state.config) return;
  showPanelBack(true);
  var loc = state.config.theme.locale || "zh-CN", wid = state.config.widgetId;
  setPanelContent("");
  if (feature === "text_chat") { renderTextChatPanel(loc, wid, state.config.receptionistId, state.config.receptionistName, renderHomeView, state.config.textChatWelcome, state.config.quickReplies, state.config.formCollection); }
  else if (feature === "voice") { renderVoicePanel(loc, wid); }
  else if (feature === "callback") { renderCallbackPanel(loc, wid); }
}

function renderActiveFeature(overrideFeature?: Feature): void {
  if (!state.config) return;
  var f = overrideFeature || state.activeFeature; if (!f) return;
  showPanelBack(true);
  var loc = state.config.theme.locale || "zh-CN", wid = state.config.widgetId;
  setPanelContent("");
  if (f === "text_chat") { renderTextChatPanel(loc, wid, state.config.receptionistId, state.config.receptionistName, renderHomeView, state.config.textChatWelcome, state.config.quickReplies, state.config.formCollection); }
  else if (f === "voice") { renderVoicePanel(loc, wid); }
  else if (f === "callback") { renderCallbackPanel(loc, wid); }
  else { renderHomeView(); }
}

function esc(s: string): string { if (!s) return ""; return s.split("&").join("&amp;").split("<").join("&lt;").split(">").join("&gt;").split("\"").join("&quot;"); }

export var getState = function(): Readonly<SDKState> { return state; };
export var openPanel = function(): void { if (!state.initialized) return; var r = getShadowRoot(), p = r?.getElementById("oc-panel"), f = r?.getElementById("oc-fab"); if (p && f) { p.classList.add("open"); f.classList.remove("pulse"); state.panelOpen = true; renderHomeView(); } };
export var closePanel = function(): void { if (!state.initialized) return; var r = getShadowRoot(), p = r?.getElementById("oc-panel"), f = r?.getElementById("oc-fab"); if (p && f) { p.classList.remove("open"); f.classList.add("pulse"); state.panelOpen = false; } };
export var createApiClient = function(): ApiClient { return new ApiClient(_apiBase); };
export var destroy = function(): void { if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; } stopWidgetGuard(); var h = document.getElementById("onecall-widget-host"); if (h) h.remove(); resetContainer(); state.initialized = false; state.config = null; };
export { renderVoicePanel, renderTextChatPanel, renderCallbackPanel };

var _existing = typeof window !== "undefined" ? ((window as any).SDK16x || (window as any)["16xSDK"] || (window as any).OneCallSDK) : null;
var _queue: any[] = _existing && Array.isArray(_existing.q) ? _existing.q.slice() : [];
if (typeof window !== "undefined") {
  console.log("[OneCallSDK] SDK 脚本已加载，版本 2.0");
  var api = { init: init, getState: getState, openPanel: openPanel, closePanel: closePanel, createApiClient: createApiClient, destroy: destroy };
  // 保留 q 属性兼容页面侧推送（防止页面反复 overwrite OneCallSDK）
  Object.defineProperty(api, "q", { value: { push: function(item: any) { var fn = (api as any)[item.method]; if (typeof fn === "function") try { fn.apply(null, item.args); } catch (e) { console.error("[OneCallSDK] q push 失败:", item.method, e); } } }, writable: false, configurable: false });
  (window as any).SDK16x = api;
  (window as any)["16xSDK"] = api;
  // 串行处理队列中的 init 调用，避免并发初始化
  (async function() {
    for (var idx = 0; idx < _queue.length; idx++) { var c = _queue[idx]; var fn = (api as any)[c.method]; if (typeof fn === "function") try { await fn.apply(null, c.args); } catch (e) { console.error("[OneCallSDK] 队列失败:", c.method, e); } }
  })();
}
